import { useCallback, useEffect } from 'react';
import type { RefObject } from 'react';

const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const EXCLUDED_ANCESTORS = '[hidden], [aria-hidden="true"], [inert], fieldset:disabled';
// react-datetime (.rdt) opens its calendar on focus, so landing initial focus there pops it open before the user does anything
const OPENS_ON_FOCUS = '[role="combobox"], [aria-haspopup], .rdt';

// mounted traps, oldest first: like stacked modals, only the newest one owns Tab and Escape
const layers: HTMLElement[] = [];

const isRendered = (element: HTMLElement): boolean => {
    if (typeof element.checkVisibility === 'function') return element.checkVisibility({ visibilityProperty: true });

    // jsdom has no checkVisibility and does not cascade display:none to descendants, so walk the ancestors
    for (let current: HTMLElement | null = element; current !== null; current = current.parentElement) {
        const style = getComputedStyle(current);
        if (style.display === 'none' || style.visibility === 'hidden') return false;
    }

    return true;
};

const tabbableIn = (container: HTMLElement): HTMLElement[] =>
    Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (element) => element.tabIndex >= 0 && element.closest(EXCLUDED_ANCESTORS) === null && isRendered(element)
    );

const isBefore = (a: Element, b: Element): boolean => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;

const isInsideNativeDialog = (element: Element | null): boolean => element?.closest('dialog[open]') !== null && element?.closest('dialog[open]') !== undefined;

// A div-based overlay has to constrain Tab itself; StandardModal gets this from the native <dialog>.showModal().
// Initial focus goes to the first control inside `initialFocusRef` (e.g. the body rather than the header's close button).
// `isTopLayer` tells the caller whether this trap currently owns the keyboard, so Escape handling can follow the same rule.
export const useFocusTrap = (containerRef: RefObject<HTMLElement | null>, initialFocusRef?: RefObject<HTMLElement | null>): { isTopLayer: () => boolean } => {
    const isTopLayer = useCallback(() => containerRef.current !== null && layers[layers.length - 1] === containerRef.current, [containerRef]);

    useEffect(() => {
        const container = containerRef.current;
        if (container === null) return;

        layers.push(container);

        const initialScope = initialFocusRef?.current ?? container;
        const initial = tabbableIn(initialScope).find((element) => element.closest(OPENS_ON_FOCUS) === null) ?? tabbableIn(container)[0];
        (initial ?? container).focus();

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Tab') return;

            // a native modal dialog above the drawers owns the keyboard; otherwise only the newest drawer does
            const active = document.activeElement;
            if (isInsideNativeDialog(active) || layers[layers.length - 1] !== container) return;

            const tabbable = tabbableIn(container);
            const first = tabbable[0];
            if (first === undefined) {
                event.preventDefault();
                container.focus();
                return;
            }

            const last = tabbable[tabbable.length - 1] ?? first;
            const isInside = active !== null && container.contains(active);
            const atStart = active === container || active === first || (active !== null && isBefore(active, first));
            const atEnd = active === last || (active !== null && isBefore(last, active));

            if (!isInside || (event.shiftKey && atStart) || (!event.shiftKey && atEnd)) {
                event.preventDefault();
                (event.shiftKey ? last : first).focus();
            }
        };

        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            const index = layers.lastIndexOf(container);
            if (index >= 0) layers.splice(index, 1);
        };
    }, [containerRef, initialFocusRef]);

    return { isTopLayer };
};
